import { useCallback, useEffect, useState } from 'react';
import useDismissibleOverlay from '../ui/useDismissibleOverlay';

interface UseRecipeViewStateResult {
  isEditing: boolean;
  isCustomFullScreen: boolean;
  isImageOverlayOpen: boolean;
  openFullScreen: () => void;
  closeFullScreen: () => void;
  openImageOverlay: () => void;
  closeImageOverlay: () => void;
  toggleEdit: () => void;
  setEditing: React.Dispatch<React.SetStateAction<boolean>>;
}

const shouldDisablePullToRefresh = (
  isCustomFullScreen: boolean,
  isImageOverlayOpen: boolean
) => isCustomFullScreen || isImageOverlayOpen;

export const useRecipeViewState = (): UseRecipeViewStateResult => {
  const [isEditing, setIsEditing] = useState(false);
  const [isCustomFullScreen, setIsCustomFullScreen] = useState(false);
  const [isImageOverlayOpen, setIsImageOverlayOpen] = useState(false);

  const openFullScreen = useCallback(() => {
    setIsCustomFullScreen(true);
  }, []);

  const closeFullScreen = useCallback(() => {
    setIsCustomFullScreen(false);
    document.body.classList.remove('no-pull-to-refresh');
  }, []);

  const openImageOverlay = useCallback(() => {
    setIsImageOverlayOpen(true);
  }, []);

  const closeImageOverlay = useCallback(() => {
    setIsImageOverlayOpen(false);
  }, []);

  const toggleEdit = useCallback(() => {
    setIsEditing((previous) => !previous);
  }, []);

  useDismissibleOverlay({
    isOpen: isImageOverlayOpen,
    onClose: closeImageOverlay,
    disablePullToRefresh: true,
  });

  useEffect(() => {
    if (shouldDisablePullToRefresh(isCustomFullScreen, isImageOverlayOpen)) {
      document.body.classList.add('no-pull-to-refresh');
      return () => {
        document.body.classList.remove('no-pull-to-refresh');
      };
    }

    document.body.classList.remove('no-pull-to-refresh');
    return undefined;
  }, [isCustomFullScreen, isImageOverlayOpen]);

  return {
    isEditing,
    isCustomFullScreen,
    isImageOverlayOpen,
    openFullScreen,
    closeFullScreen,
    openImageOverlay,
    closeImageOverlay,
    toggleEdit,
    setEditing: setIsEditing,
  };
};

export default useRecipeViewState;
